import { useMemo } from 'react';
import { ScheduleRequest } from '@weave/schema-gen-ts/dist/schemas/schedule/api/v2/api.pb';
import dayjs from 'dayjs';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { useTranslation } from '@frontend/i18n';
import { useLocationTimezone } from '@frontend/schedule-helpers';
import { useForm, useFormField } from '@frontend/design-system';
import { MIN_APPT_TYPE_DURATION_IN_MINUTES } from '../../../../../../constants';
import { getMaxCalendarDate } from '../../../../../../utils';
import { getTimeDiff } from '../../../../../../utils/date-helpers';
import { isValidEndTime } from '../../../../helpers';
import { ReviewRequestReturnType } from './useReviewRequest';

export const useManageScheduleRequestModalForm = (
  selectedScheduleRequest: ScheduleRequest,
  isSourceFieldRequired: boolean,
  appointmentTypes: ReviewRequestReturnType['lists']['appointmentTypes'],
  providers: ReviewRequestReturnType['lists']['providers']
) => {
  const { t } = useTranslation('scheduleCalendarRequest');
  const { isScheduleV3FlagEnabled } = SchedulerV3.Helpers.useGetSchedulerV3FeatureFlagDetails();
  const { formatDateAndTime } = useLocationTimezone(selectedScheduleRequest.locationId);

  const { archivedApptTypeIds, archivedProviderIds } = useMemo(() => {
    const archivedApptTypeIds = isScheduleV3FlagEnabled
      ? appointmentTypes.filter((apptType) => !apptType.active).map((apptType) => apptType.id)
      : [];
    const archivedProviderIds = isScheduleV3FlagEnabled
      ? providers.filter((provider) => !provider.isActive).map((provider) => provider.id)
      : [];
    return { archivedApptTypeIds, archivedProviderIds };
  }, [appointmentTypes, isScheduleV3FlagEnabled, providers]);

  const scheduleRequestApprovalForm = useForm({
    fields: {
      appointmentType: {
        type: 'dropdown',
        value: '',
        required: true,
        validator: ({ value }) => (archivedApptTypeIds.includes(value) ? t('Please select an active type') : ''),
      },
      dateAndTime: {
        type: 'dropdown',
        value: selectedScheduleRequest?.requestedOpenings?.[0]?.dateTime ?? '',
        required: true,
      },
      provider: {
        type: 'dropdown',
        value: '',
        required: true,
        validator: ({ value }) => (archivedProviderIds.includes(value) ? t('Please select an active provider') : ''),
      },
      source: {
        type: 'dropdown',
        value: '',
        required: isSourceFieldRequired,
      },
      workstation: {
        type: 'dropdown',
        value: '',
      },
    },
  });

  const isCustomDateTime = scheduleRequestApprovalForm.values.dateAndTime === 'custom';

  const appointmentTypeDuration = useMemo(() => {
    const selectedApptType = appointmentTypes.find((item) => {
      const id = 'bookableAppointmentTypeId' in item ? item.bookableAppointmentTypeId ?? item.id : item.id;
      return id === scheduleRequestApprovalForm.values.appointmentType;
    });
    return selectedApptType?.durationMinutes || MIN_APPT_TYPE_DURATION_IN_MINUTES;
  }, [appointmentTypes, scheduleRequestApprovalForm.values.appointmentType]);

  const customDateField = useFormField(
    {
      type: 'date',
      value: formatDateAndTime(selectedScheduleRequest?.requestedOpenings?.[0]?.dateTime, 'MM/DD/YYYY'),
      required: isCustomDateTime,
      maxDate: getMaxCalendarDate(),
    },
    [scheduleRequestApprovalForm.values.dateAndTime]
  );

  const customStartTimeField = useFormField(
    {
      type: 'time',
      value: formatDateAndTime(selectedScheduleRequest?.requestedOpenings?.[0]?.dateTime, 'hh:mm a'),
      required: isCustomDateTime,
    },
    [scheduleRequestApprovalForm.values.dateAndTime]
  );

  const customEndTimeField = useFormField(
    {
      type: 'time',
      value: formatDateAndTime(
        dayjs(selectedScheduleRequest?.requestedOpenings?.[0]?.dateTime)
          .add(appointmentTypeDuration || MIN_APPT_TYPE_DURATION_IN_MINUTES, 'minutes')
          .utc(),
        'hh:mm a'
      ),
      required: isCustomDateTime,
    },
    [scheduleRequestApprovalForm.values.dateAndTime, appointmentTypeDuration, selectedScheduleRequest.duration]
  );

  const customDurationV3 = useMemo(() => {
    // If the selected appointment type has a duration of 0, and the start/end times are the same, then use the default 15 minutes instead of calculating it to 1440 minutes.
    if (appointmentTypeDuration === 0 && customStartTimeField.value === customEndTimeField.value) {
      return MIN_APPT_TYPE_DURATION_IN_MINUTES;
    }

    return getTimeDiff(customStartTimeField.value, customEndTimeField.value, { crossDay: true });
  }, [customEndTimeField, customStartTimeField, appointmentTypeDuration]);

  const customDuration = useMemo(() => {
    const startTime = dayjs(`${customDateField.value} ${customStartTimeField.value}`);
    const endTime = dayjs(`${customDateField.value} ${customEndTimeField.value}`);
    return endTime.diff(startTime, 'minute');
  }, [customDateField.value, customStartTimeField.value, customEndTimeField.value]);

  const isValidCustomEndTime =
    isCustomDateTime &&
    isValidEndTime({
      date: customDateField.value,
      startTime: customStartTimeField.value,
      endTime: customEndTimeField.value,
    });

  return {
    scheduleRequestApprovalForm,
    customDateField,
    customStartTimeField,
    customEndTimeField,
    appointmentDuration: isScheduleV3FlagEnabled ? customDurationV3 : customDuration,
    isValidEndTime: isCustomDateTime ? isValidCustomEndTime : true,
  };
};
